import { t as MuseError } from "./muse-error-BO65y6cz.js";
import { n as toCanonicalCharacteristicUuid } from "./uuid-D9bCf1aR.js";
//#region src/protocol/commands.ts
/**
* The control vocabulary, as established by the reference implementations and
* confirmed against firmware 3.1.19.
*
* Small on purpose: these are every token muse-js, amused-py and OpenMuse send
* between them, and every one of them was acknowledged by live hardware. A
* token absent from here is not necessarily invalid — it is merely unattested,
* and `encodeCommand` will still send it.
*
* `version` is `v6` on the Athena and `v1` on earlier hardware; both request
* the device-information reply, which arrives asynchronously on the control
* characteristic like every other status.
*/
const MUSE_COMMANDS = {
	/** Request the device-information reply. Athena. */
	version: "v6",
	/** Request the device-information reply. Pre-Athena hardware. */
	versionLegacy: "v1",
	/** Request a status reply: battery, preset, serial, firmware. */
	status: "s",
	/** Halt streaming. The connection stays open. */
	halt: "h",
	/** Resume streaming after `halt`, without reapplying a preset. */
	resume: "d",
	/**
	* Start the data stream on the Athena.
	*
	* Must be sent **twice**: both published implementations agree the device
	* ignores the first, and hardware confirms it.
	*/
	startStreaming: "dc001",
	/** Low-latency mode. Sent after `startStreaming`. */
	lowLatency: "L1"
};
/**
* The preset command for a token, e.g. `"p1041"`.
*
* A pass-through that exists so call sites name their intent rather than
* passing a bare string, and so the one place presets are turned into commands
* is greppable. Validation belongs to `isPresetToken`, which knows the grammar.
*/
function presetCommand(preset) {
	return preset;
}
/** Longest command the single length byte can describe. */
const MAX_COMMAND_LENGTH = 253;
const NEWLINE = 10;
/**
* Encodes a control command for the control characteristic.
*
* Wire format: `[length] [ASCII command bytes] [0x0A]`
*
* @throws {MuseError} `MALFORMED_PACKET` if the command is empty, non-ASCII,
*   contains a newline of its own, or is too long for the length byte.
*/
function encodeCommand(command, options = {}) {
	if (command.length === 0) throw new MuseError("MALFORMED_PACKET", "Command must not be empty");
	if (command.length > MAX_COMMAND_LENGTH) throw new MuseError("MALFORMED_PACKET", `Command exceeds ${MAX_COMMAND_LENGTH} characters (got ${command.length})`, { details: { command: command.slice(0, 32) } });
	const frame = new Uint8Array(command.length + 2);
	for (let i = 0; i < command.length; i++) {
		const code = command.charCodeAt(i);
		if (code > 127) throw new MuseError("MALFORMED_PACKET", `Command must be ASCII (offending char at ${i})`);
		if (code === NEWLINE) throw new MuseError("MALFORMED_PACKET", "Command must not contain a newline");
		frame[i + 1] = code;
	}
	const payloadLength = command.length + 1;
	frame[0] = (options.lengthPrefix ?? "payload") === "payloadPlusPrefix" ? payloadLength + 1 : payloadLength;
	frame[frame.length - 1] = NEWLINE;
	return frame;
}
/**
* Reads back a frame produced by {@link encodeCommand}.
* Used by the replay transport and by tests; not part of the streaming path.
*/
function decodeCommand(frame) {
	if (frame.length < 2) throw new MuseError("MALFORMED_PACKET", `Command frame too short (${frame.length} bytes)`);
	if (frame[frame.length - 1] !== NEWLINE) throw new MuseError("MALFORMED_PACKET", "Command frame is not newline-terminated");
	let out = "";
	for (let i = 1; i < frame.length - 1; i++) out += String.fromCharCode(frame[i]);
	return out;
}
//#endregion
//#region src/protocol/athena/channels.ts
/**
* EEG channel layout for the Muse S Athena.
*
* Positions follow the 10–20 system. The order below is the order values appear
* in a decoded EEG subpacket, and it has been **verified on hardware** rather
* than taken on trust: with the headband worn, winking one eye raised the
* corresponding frontal channel far above rest while leaving the other
* comparatively untouched — AF7 rose 6.4× on a left wink, AF8 rose 8.3× on a
* right wink. A transposed layout could not produce that asymmetry.
*/
const ATHENA_EEG_CHANNELS_4 = [
	"TP9",
	"AF7",
	"AF8",
	"TP10"
];
const ATHENA_EEG_CHANNELS_8 = [
	"TP9",
	"AF7",
	"AF8",
	"TP10",
	"AUX1",
	"AUX2",
	"AUX3",
	"AUX4"
];
const ATHENA_CHANNEL_INFO = {
	TP9: {
		label: "TP9",
		site: "left ear",
		scalp: true
	},
	AF7: {
		label: "AF7",
		site: "left forehead",
		scalp: true
	},
	AF8: {
		label: "AF8",
		site: "right forehead",
		scalp: true
	},
	TP10: {
		label: "TP10",
		site: "right ear",
		scalp: true
	},
	AUX1: {
		label: "AUX1",
		site: "auxiliary input 1",
		scalp: false
	},
	AUX2: {
		label: "AUX2",
		site: "auxiliary input 2",
		scalp: false
	},
	AUX3: {
		label: "AUX3",
		site: "auxiliary input 3",
		scalp: false
	},
	AUX4: {
		label: "AUX4",
		site: "auxiliary input 4",
		scalp: false
	}
};
/**
* Labels for an EEG subpacket of the given width.
*
* **The four auxiliary channels are inputs for external electrodes, not extra
* scalp sensors.** Measured on a worn headband with nothing plugged in, they
* showed no response whatsoever to blinks, winks or jaw movement — 0.87 to 1.09
* times resting amplitude, where the scalp channels moved by factors of 6 to 8.
* Their amplitude stayed flat across every segment of the protocol. AUX4 in
* particular sat at the positive rail for more than half of all samples.
*
* So an 8-channel preset does not give eight channels of brain activity. It
* gives four, plus four inputs that are only useful with electrodes attached.
* Treating them as scalp data would put pure noise into an analysis.
*/
function eegChannelLabels(count) {
	if (count === 4) return ATHENA_EEG_CHANNELS_4;
	if (count === 8) return ATHENA_EEG_CHANNELS_8;
	return ATHENA_EEG_CHANNELS_8.slice(0, count);
}
/** The subset that measures scalp potential without external electrodes. */
function scalpChannelIndices(count) {
	return eegChannelLabels(count).map((label, index) => ({
		label,
		index
	})).filter(({ label }) => ATHENA_CHANNEL_INFO[label].scalp).map(({ index }) => index);
}
//#endregion
//#region src/protocol/athena/characteristics.ts
/**
* The characteristics a Muse S Athena exposes under service `0xFE8D`.
*
* Kept here rather than repeated as string literals at each call site, because
* the distinction matters: the control channel carries length-prefixed JSON
* fragments, and feeding those to the packet decoder produces a stream of
* plausible-looking nonsense rather than an obvious failure.
*/
/** Service the headband advertises. */
const MUSE_SERVICE_UUID_16 = 65165;
const MUSE_SERVICE_UUID = "0000fe8d-0000-1000-8000-00805f9b34fb";
/** Write commands here; JSON replies arrive as notifications on the same handle. */
const ATHENA_CONTROL_CHARACTERISTIC = "273e0001-4c4d-454d-96be-f03bac821358";
/** The multiplexed sensor stream. Everything of substance arrives here. */
const ATHENA_UNIVERSAL_CHARACTERISTIC = "273e0013-4c4d-454d-96be-f03bac821358";
/**
* Two further channels the headband exposes.
*
* `0014` is documented by other projects; `0015` was found on firmware 3.1.19
* and appears in no published source. Both accept a subscription and have so far
* stayed silent during streaming (K6). Worth subscribing to anyway — a recording
* that omitted them is the one we would later wish we had.
*/
const ATHENA_AUX_CHARACTERISTICS = ["273e0014-4c4d-454d-96be-f03bac821358", "273e0015-4c4d-454d-96be-f03bac821358"];
/** Every characteristic that may carry framed sensor packets. */
const ATHENA_DATA_CHARACTERISTICS = [ATHENA_UNIVERSAL_CHARACTERISTIC, ...ATHENA_AUX_CHARACTERISTICS];
const DATA_SET = new Set(ATHENA_DATA_CHARACTERISTICS.map((uuid) => toCanonicalCharacteristicUuid(uuid)));
/**
* Whether a characteristic carries packets the Athena frame codec understands.
*
* Use this to filter before decoding. The control channel will otherwise be
* parsed as sensor data and yield anomalies that look like a protocol problem
* but are only a misrouted stream.
*/
function isAthenaDataCharacteristic(uuid) {
	return DATA_SET.has(toCanonicalCharacteristicUuid(uuid));
}
function isAthenaControlCharacteristic(uuid) {
	return toCanonicalCharacteristicUuid(uuid) === ATHENA_CONTROL_CHARACTERISTIC;
}
//#endregion
//#region src/protocol/athena/bit-reader.ts
/**
* Powers of two up to 2^32, precomputed.
*
* The reader deliberately uses float arithmetic rather than `<<`/`>>>`:
* JavaScript's bitwise operators coerce to *signed* 32-bit, which silently
* corrupts any accumulator that crosses bit 31. Every intermediate here stays
* below 2^27, far inside the exactly-representable integer range, so
* multiply/divide by a power of two is both exact and fast.
*/
const POW2 = Array.from({ length: 33 }, (_, i) => 2 ** i);
/** Widest field the Athena protocol packs (20-bit optics). */
const MAX_FIELD_WIDTH = 24;
/**
* Sequential reader for LSB-first packed bit fields.
*
* Bit `n` of the stream is bit `n % 8` of byte `n / 8`, counting from the least
* significant bit of each byte. The first bit read becomes the least
* significant bit of the produced value — i.e. a little-endian bit stream.
*
* Fields are read back-to-back with no padding, which is why a sequential
* reader is the natural shape: it never recomputes a byte offset.
*/
var LsbBitReader = class {
	#data;
	#end;
	#bytePos;
	/** Bits pulled from the stream but not yet consumed, right-aligned. */
	#acc = 0;
	/** How many bits {@link #acc} currently holds. */
	#bits = 0;
	constructor(data, byteOffset = 0, byteLength = data.length - byteOffset) {
		if (!Number.isInteger(byteOffset) || !Number.isInteger(byteLength) || byteOffset < 0 || byteLength < 0 || byteOffset + byteLength > data.length) throw new MuseError("MALFORMED_PACKET", `Bit range [${byteOffset}, ${byteOffset + byteLength}) lies outside a ${data.length}-byte buffer`);
		this.#data = data;
		this.#bytePos = byteOffset;
		this.#end = byteOffset + byteLength;
	}
	/** Bits not yet consumed. */
	get remaining() {
		return (this.#end - this.#bytePos) * 8 + this.#bits;
	}
	/**
	* Reads the next `width`-bit unsigned field.
	*
	* @throws {MuseError} `MALFORMED_PACKET` when the stream runs out.
	*/
	read(width) {
		if (width <= 0 || width > 24) throw new MuseError("MALFORMED_PACKET", `Unsupported field width ${width}`);
		while (this.#bits < width) {
			if (this.#bytePos >= this.#end) throw new MuseError("MALFORMED_PACKET", `Ran out of bits: needed ${width}, have ${this.#bits}`, { details: {
				width,
				available: this.#bits
			} });
			this.#acc += this.#data[this.#bytePos++] * POW2[this.#bits];
			this.#bits += 8;
		}
		const divisor = POW2[width];
		const value = this.#acc % divisor;
		this.#acc = (this.#acc - value) / divisor;
		this.#bits -= width;
		return value;
	}
	/**
	* Reads `count` fields of `width` bits into `target`.
	*
	* The bulk form exists because this runs 256 times a second per channel;
	* hoisting the bounds check and the power lookup out of the loop measurably
	* beats calling {@link read} in a loop.
	*/
	readInto(target, count, width) {
		if (width <= 0 || width > 24) throw new MuseError("MALFORMED_PACKET", `Unsupported field width ${width}`);
		if (count > target.length) throw new MuseError("MALFORMED_PACKET", `Target holds ${target.length} values, asked for ${count}`);
		if (this.remaining < count * width) throw new MuseError("MALFORMED_PACKET", `Need ${count * width} bits, only ${this.remaining} remain`, { details: {
			count,
			width,
			remaining: this.remaining
		} });
		const divisor = POW2[width];
		for (let i = 0; i < count; i++) {
			while (this.#bits < width) {
				this.#acc += this.#data[this.#bytePos++] * POW2[this.#bits];
				this.#bits += 8;
			}
			const value = this.#acc % divisor;
			this.#acc = (this.#acc - value) / divisor;
			this.#bits -= width;
			target[i] = value;
		}
	}
};
//#endregion
//#region src/protocol/athena/tags.ts
const SPECS = [
	{
		tag: 17,
		kind: "eeg",
		channels: 4,
		samples: 4,
		nominalRate: 256,
		bitWidth: 14,
		dataLength: 28
	},
	{
		tag: 18,
		kind: "eeg",
		channels: 8,
		samples: 2,
		nominalRate: 256,
		bitWidth: 14,
		dataLength: 28
	},
	{
		tag: 52,
		kind: "optics",
		channels: 4,
		samples: 3,
		nominalRate: 64,
		bitWidth: 20,
		dataLength: 30
	},
	{
		tag: 53,
		kind: "optics",
		channels: 8,
		samples: 2,
		nominalRate: 64,
		bitWidth: 20,
		dataLength: 40
	},
	{
		tag: 54,
		kind: "optics",
		channels: 16,
		samples: 1,
		nominalRate: 64,
		bitWidth: 20,
		dataLength: 40
	},
	{
		tag: 71,
		kind: "imu",
		channels: 6,
		samples: 3,
		nominalRate: 52,
		bitWidth: 16,
		dataLength: 36
	},
	{
		tag: 83,
		kind: "drlref",
		channels: 2,
		samples: 6,
		nominalRate: 30,
		bitWidth: 16,
		dataLength: 24
	},
	{
		tag: 136,
		kind: "battery",
		channels: 1,
		samples: 1,
		nominalRate: 1,
		bitWidth: null,
		dataLength: 20
	},
	{
		tag: 152,
		kind: "battery",
		channels: 1,
		samples: 1,
		nominalRate: 1,
		bitWidth: null,
		dataLength: 20
	}
];
const BY_TAG = new Map(SPECS.map((s) => [s.tag, s]));
const ATHENA_SENSOR_SPECS = SPECS;
function specForTag(tag) {
	return BY_TAG.get(tag);
}
function isKnownTag(tag) {
	return BY_TAG.has(tag);
}
/** Bytes of packet header preceding the data section. */
const PACKET_HEADER_SIZE = 14;
/** Bytes of subpacket header: TAG + index + three unidentified bytes. */
const SUBPACKET_HEADER_SIZE = 5;
//#endregion
//#region src/protocol/athena/frame-codec.ts
/**
* Splits one BLE notification into packets and subpackets.
*
* Structure:
* ```
* notification
*   packet[]                        declared length in byte 0
*     14-byte header
*     leading subpacket             no TAG, no header; type is the packet id
*     [TAG][index][3 reserved][payload]   repeated
* ```
*
* Decoding is total: it never throws on malformed input, because a headband
* that emits one bad byte should not take the session down. Structural
* problems surface as {@link AthenaAnomaly} entries alongside whatever was
* recovered.
*/
function decodeAthenaNotification(payload) {
	const packets = [];
	const anomalies = [];
	let offset = 0;
	while (offset < payload.length) {
		if (offset + 14 > payload.length) {
			anomalies.push({
				kind: "trailing-bytes",
				byteOffset: offset,
				message: `${payload.length - offset} byte(s) left, too few for a packet header`
			});
			break;
		}
		const declaredLength = payload[offset];
		if (declaredLength < 14) {
			anomalies.push({
				kind: "impossible-length",
				byteOffset: offset,
				message: `Declared packet length ${declaredLength} is below the 14-byte header`
			});
			break;
		}
		if (offset + declaredLength > payload.length) {
			anomalies.push({
				kind: "truncated-packet",
				byteOffset: offset,
				message: `Packet claims ${declaredLength} bytes, only ${payload.length - offset} available`
			});
			break;
		}
		packets.push(readPacket(payload, offset, declaredLength, anomalies));
		offset += declaredLength;
	}
	return {
		packets,
		anomalies
	};
}
function readPacket(payload, start, length, anomalies) {
	const index = payload[start + 1] + payload[start + 2] * 256;
	const deviceTicks = payload[start + 3] + payload[start + 4] * 256 + payload[start + 5] * 65536;
	const packetId = payload[start + 9];
	const dataStart = start + 14;
	const dataEnd = start + length;
	const subpackets = [];
	let cursor = dataStart;
	let unparsedTail = null;
	const leadSpec = specForTag(packetId);
	if (leadSpec === void 0) {
		anomalies.push({
			kind: "unknown-packet-id",
			byteOffset: start + 9,
			message: `Unknown packet id 0x${packetId.toString(16).padStart(2, "0")}`
		});
		unparsedTail = payload.subarray(dataStart, dataEnd);
		cursor = dataEnd;
	} else {
		const leadLength = leadSpec.dataLength === "toEndOfPacket" ? dataEnd - cursor : leadSpec.dataLength;
		if (cursor + leadLength <= dataEnd) {
			subpackets.push({
				tag: packetId,
				kind: leadSpec.kind,
				spec: leadSpec,
				index: null,
				reserved: null,
				data: payload.subarray(cursor, cursor + leadLength)
			});
			cursor += leadLength;
		} else {
			anomalies.push({
				kind: "truncated-packet",
				byteOffset: cursor,
				message: `Leading subpacket 0x${packetId.toString(16)} needs ${leadLength} bytes, ${dataEnd - cursor} present`
			});
			unparsedTail = payload.subarray(cursor, dataEnd);
			cursor = dataEnd;
		}
	}
	while (cursor + 5 <= dataEnd) {
		const tag = payload[cursor];
		const spec = specForTag(tag);
		if (spec === void 0) {
			anomalies.push({
				kind: "unknown-subpacket-tag",
				byteOffset: cursor,
				message: `Unknown subpacket tag 0x${tag.toString(16).padStart(2, "0")}; cannot determine its length`
			});
			break;
		}
		const payloadStart = cursor + 5;
		const dataLength = spec.dataLength === "toEndOfPacket" ? dataEnd - payloadStart : spec.dataLength;
		if (payloadStart + dataLength > dataEnd) {
			anomalies.push({
				kind: "truncated-packet",
				byteOffset: cursor,
				message: `Subpacket 0x${tag.toString(16)} needs ${dataLength} bytes, ${dataEnd - payloadStart} present`
			});
			break;
		}
		subpackets.push({
			tag,
			kind: spec.kind,
			spec,
			index: payload[cursor + 1],
			reserved: payload.subarray(cursor + 2, cursor + 5),
			data: payload.subarray(payloadStart, payloadStart + dataLength)
		});
		cursor = payloadStart + dataLength;
	}
	if (unparsedTail === null && cursor < dataEnd) unparsedTail = payload.subarray(cursor, dataEnd);
	return {
		length,
		index,
		deviceTicks,
		packetId,
		reserved1: payload.subarray(start + 6, start + 9),
		sensorSequence: payload[start + 10],
		reserved2: payload.subarray(start + 11, start + 13),
		byte13: payload[start + 13],
		subpackets,
		unparsedTail
	};
}
/** Convenience for call sites that want a hard failure instead of anomalies. */
function decodeAthenaNotificationStrict(payload) {
	const { packets, anomalies } = decodeAthenaNotification(payload);
	if (anomalies.length > 0) {
		const first = anomalies[0];
		throw new MuseError("MALFORMED_PACKET", `${first.kind} at byte ${first.byteOffset}: ${first.message}`, { details: { anomalies } });
	}
	return packets;
}
//#endregion
//#region src/protocol/athena/scaling.ts
/**
* Conversion from raw device counts to physical units.
*
* These constants come from the published community decoders (amused-py,
* OpenMuse), which agree with each other. Two of them are still open questions
* — see the notes below and `docs/protocol.md`.
*/
const ATHENA_SCALING = {
	/**
	* Microvolts per EEG count. 14-bit range spans 1450 µV.
	*/
	eegMicrovoltsPerCount: 1450 / 16383,
	/**
	* Count that corresponds to 0 µV: mid-scale of the 14-bit range.
	*
	* **Measured, resolving K2.** With the headband worn, all four scalp channels
	* sit on mid-scale — median 8179–8264 counts across TP9, AF7, AF8 and TP10 —
	* and their robust spread is 10–34 µV, which is ordinary EEG. Subtracting
	* 8192 puts a resting signal at +6 µV; leaving it out puts it at +731 µV,
	* which is physically impossible for scalp EEG.
	*
	* The published Python decoders apply no offset. Their output is therefore
	* shifted by about 725 µV. That washes out of any spectral analysis, since a
	* high-pass or band-pass filter removes a DC term, but it makes raw amplitudes
	* and anything threshold-based wrong.
	*
	* The comparison that settles it: the same headband lying on a desk rails
	* across the entire range (38 % of samples at 0 or 16383, median absolute
	* deviation 7075 counts), whereas worn it does not (0.6 %, MAD 382).
	*/
	eegZeroCount: 8192,
	/** g per accelerometer count (16-bit signed). */
	accelerometerGPerCount: 610352e-10,
	/**
	* Degrees per second per gyroscope count (16-bit signed).
	* The sign is negative: the device's convention is inverted relative to the
	* axis order it reports.
	*/
	gyroscopeDegPerSecondPerCount: -.0074768,
	/**
	* Optics scale factor.
	*
	* **Open question K3.** Applied to 20-bit counts this yields roughly 0…32 in
	* unspecified units — it is a normalisation, not a calibration to microamps.
	* Optical output is therefore reported as arbitrary units, and absolute
	* comparison between devices or sessions is not supported.
	*/
	opticsPerCount: 1 / 32768,
	/** Battery state-of-charge: raw u16 divided by this gives percent. */
	batteryPercentDivisor: 256
};
//#endregion
//#region src/protocol/athena/decode-samples.ts
/** Decodes 14-bit unsigned LSB-packed EEG counts. */
function decodeEegCounts(subpacket) {
	const { channels, samples } = subpacket.spec;
	return unpackFields(subpacket, channels, samples, 14);
}
/** Decodes 20-bit unsigned LSB-packed optical counts. */
function decodeOpticsCounts(subpacket) {
	const { channels, samples } = subpacket.spec;
	return unpackFields(subpacket, channels, samples, 20);
}
/**
* Decodes the DRL/REF pair: 16-bit unsigned little-endian words, sample-major.
*
* Unlike EEG and optics this payload is **not** bit-packed — each value
* occupies a whole 16-bit word with the top two bits unused, which is how the
* layout was recognised. Values share the EEG converter's mid-scale, so
* {@link eegCountsToMicrovolts} converts them.
*/
function decodeDrlRefCounts(subpacket) {
	const { channels, samples } = subpacket.spec;
	const total = channels * samples;
	const needed = total * 2;
	const data = subpacket.data;
	if (data.length < needed) throw new MuseError("MALFORMED_PACKET", `DRL/REF subpacket needs ${needed} bytes, got ${data.length}`, { details: {
		tag: subpacket.tag,
		length: data.length
	} });
	const counts = new Int32Array(total);
	for (let i = 0; i < total; i++) counts[i] = data[i * 2] | data[i * 2 + 1] << 8;
	return {
		channels,
		samples,
		counts
	};
}
/**
* Decodes 16-bit signed little-endian IMU counts.
*
* Channel order within a sample is X, Y, Z accelerometer followed by
* X, Y, Z gyroscope.
*/
function decodeImuCounts(subpacket) {
	const { channels, samples } = subpacket.spec;
	const total = channels * samples;
	const needed = total * 2;
	const data = subpacket.data;
	if (data.length < needed) throw new MuseError("MALFORMED_PACKET", `IMU subpacket needs ${needed} bytes, got ${data.length}`, { details: {
		tag: subpacket.tag,
		length: data.length
	} });
	const counts = new Int32Array(total);
	for (let i = 0; i < total; i++) {
		const unsigned = data[i * 2] + data[i * 2 + 1] * 256;
		counts[i] = unsigned >= 32768 ? unsigned - 65536 : unsigned;
	}
	return {
		channels,
		samples,
		counts
	};
}
/**
* Extracts battery state of charge.
*
* Both the older `0x98` subpacket and the newer `0x88` — [M] 20 bytes each —
* carry the charge in their first two bytes; the remaining 18 are not yet
* identified (K5).
*/
function decodeBatteryPercent(subpacket) {
	const data = subpacket.data;
	if (data.length < 2) throw new MuseError("MALFORMED_PACKET", `Battery subpacket too short (${data.length} bytes)`);
	return (data[0] + data[1] * 256) / ATHENA_SCALING.batteryPercentDivisor;
}
function unpackFields(subpacket, channels, samples, width) {
	const total = channels * samples;
	const counts = new Int32Array(total);
	const reader = new LsbBitReader(subpacket.data);
	try {
		reader.readInto(counts, total, width);
	} catch (cause) {
		throw MuseError.wrap(cause, "MALFORMED_PACKET", `Failed to unpack ${total} × ${width}-bit values from tag 0x${subpacket.tag.toString(16)}`);
	}
	return {
		channels,
		samples,
		counts
	};
}
/** Converts EEG counts to microvolts. */
function eegCountsToMicrovolts(counts, out = new Float32Array(counts.length)) {
	const { eegMicrovoltsPerCount, eegZeroCount } = ATHENA_SCALING;
	for (let i = 0; i < counts.length; i++) out[i] = (counts[i] - eegZeroCount) * eegMicrovoltsPerCount;
	return out;
}
/**
* Converts IMU counts to g and degrees per second.
*
* The two scale factors differ, so the channel layout matters: the first half of
* each sample is accelerometer, the second half gyroscope.
*/
function imuCountsToPhysical(block, out = new Float32Array(block.counts.length)) {
	const { accelerometerGPerCount, gyroscopeDegPerSecondPerCount } = ATHENA_SCALING;
	const axesPerKind = block.channels / 2;
	for (let s = 0; s < block.samples; s++) {
		const base = s * block.channels;
		for (let c = 0; c < block.channels; c++) {
			const scale = c < axesPerKind ? accelerometerGPerCount : gyroscopeDegPerSecondPerCount;
			out[base + c] = block.counts[base + c] * scale;
		}
	}
	return out;
}
/** Converts optical counts to normalised arbitrary units. See K3. */
function opticsCountsToUnits(counts, out = new Float32Array(counts.length)) {
	const { opticsPerCount } = ATHENA_SCALING;
	for (let i = 0; i < counts.length; i++) out[i] = counts[i] * opticsPerCount;
	return out;
}
//#endregion
//#region src/protocol/athena/presets.ts
const EEG4 = {
	channels: 4,
	tag: 17
};
const EEG8 = {
	channels: 8,
	tag: 18
};
const OPT4 = {
	channels: 4,
	tag: 52
};
const OPT8 = {
	channels: 8,
	tag: 53
};
const OPT16 = {
	channels: 16,
	tag: 54
};
/**
* Legacy preset tokens are accepted by the Athena, but all six collapse to the
* same configuration: four EEG channels plus IMU, and **no optical data at all**.
*
* This matters in practice: on earlier hardware `p50`/`p51`/`p60`/`p61` are the
* PPG-bearing presets. On the Athena they are not — asking for `p61` and
* expecting heart rate yields nothing. Use a `p10xx` preset for optics.
*/
const LEGACY_NOTE = "Legacy preset token; on the Athena it yields 4-channel EEG and IMU only, with no optical data.";
const LEGACY_SIBLINGS = [
	"p20",
	"p21",
	"p50",
	"p51",
	"p60",
	"p61"
];
function legacy(id) {
	return {
		id,
		eeg: EEG4,
		optics: null,
		imu: true,
		battery: true,
		opticalPower: "off",
		verified: true,
		note: LEGACY_NOTE,
		indistinguishableFrom: LEGACY_SIBLINGS.filter((s) => s !== id)
	};
}
const PROFILES = [
	...LEGACY_SIBLINGS.map(legacy),
	{
		id: "p1021",
		eeg: EEG4,
		optics: null,
		imu: true,
		battery: true,
		opticalPower: "off",
		verified: false,
		note: "The factory default. The **token** is established by measurement: live hardware reported `ps: 4129`, and 4129 decimal is 0x1021 (see docs/protocol.md). The stream shape below is the expectation that follows from a 4-channel non-optical preset and has **not** yet been captured, so it is marked unverified."
	},
	{
		id: "p1034",
		eeg: EEG4,
		optics: OPT8,
		imu: true,
		battery: true,
		opticalPower: "high",
		verified: true
	},
	{
		id: "p1035",
		eeg: EEG4,
		optics: OPT4,
		imu: true,
		battery: true,
		opticalPower: "low",
		verified: true
	},
	{
		id: "p1041",
		eeg: EEG8,
		optics: OPT16,
		imu: true,
		battery: true,
		opticalPower: "low",
		verified: true,
		indistinguishableFrom: ["p1042"]
	},
	{
		id: "p1042",
		eeg: EEG8,
		optics: OPT16,
		imu: true,
		battery: true,
		opticalPower: "high",
		verified: true,
		indistinguishableFrom: ["p1041"]
	},
	{
		id: "p1043",
		eeg: EEG8,
		optics: OPT8,
		imu: true,
		battery: true,
		opticalPower: "high",
		verified: true,
		indistinguishableFrom: ["p1044"]
	},
	{
		id: "p1044",
		eeg: EEG8,
		optics: OPT8,
		imu: true,
		battery: true,
		opticalPower: "low",
		verified: true,
		indistinguishableFrom: ["p1043"]
	},
	{
		id: "p1045",
		eeg: EEG8,
		optics: OPT4,
		imu: true,
		battery: true,
		opticalPower: "low",
		verified: true,
		indistinguishableFrom: ["p1046", "p4129"]
	},
	{
		id: "p1046",
		eeg: EEG8,
		optics: OPT4,
		imu: true,
		battery: true,
		opticalPower: "off",
		verified: true,
		indistinguishableFrom: ["p1045", "p4129"]
	},
	{
		id: "p4129",
		eeg: EEG8,
		optics: OPT4,
		imu: true,
		battery: true,
		opticalPower: "unknown",
		verified: true,
		indistinguishableFrom: ["p1045", "p1046"],
		note: "Observed to produce the same stream as p1045/p1046. Most likely the device did not accept this token and stayed in its previous configuration — a reminder that a preset command is not acknowledged and must be verified against the data that follows."
	}
];
const BY_ID = new Map(PROFILES.map((p) => [p.id, p]));
const ATHENA_PRESET_PROFILES = PROFILES;
function profileForPreset(id) {
	return BY_ID.get(id);
}
/**
* Whether a string is shaped like a preset token the firmware could parse.
*
* The registry above lists only the presets whose behaviour is known here, and
* the device accepts more than that. Rejecting an unlisted token outright would
* make this library the limit rather than the hardware, so validation is
* structural: `p` followed by two to four hexadecimal digits.
*
* The grammar is hexadecimal because measurement says so — the status reply's
* `ps` field is decimal while command tokens are hex, which is how `p4129`
* turned out to be a request for 0x4129 rather than the default 0x1021.
*/
function isPresetToken(id) {
	return /^p[0-9a-fA-F]{2,4}$/.test(id);
}
/**
* Smallest preset that supplies everything requested, or `undefined` if nothing does.
*
* Prefers fewer channels when several qualify: a narrower stream costs less
* bandwidth and drops fewer packets. Unverified profiles are considered only
* when nothing verified fits, so a caller is never silently handed an
* expectation where a measurement was available.
*/
function selectPreset(want) {
	return PROFILES.filter((p) => {
		if (want.eegChannels !== void 0 && p.eeg?.channels !== want.eegChannels) return false;
		if (want.opticsChannels === 0 && p.optics !== null) return false;
		if (want.opticsChannels !== void 0 && want.opticsChannels !== 0) {
			if (p.optics === null || p.optics.channels < want.opticsChannels) return false;
		}
		if (want.imu === true && !p.imu) return false;
		return true;
	}).toSorted((a, b) => Number(b.verified) - Number(a.verified) || (a.optics?.channels ?? 0) - (b.optics?.channels ?? 0) || a.id.localeCompare(b.id))[0];
}
//#endregion
//#region src/protocol/athena/device-clock.ts
/**
* Rate of the 24-bit counter in header bytes 3–5 — which is **not** a constant.
*
* Measured by regressing header ticks against host arrival time:
*
* - Firmware through the community captures: **1 kHz**, one tick per
*   millisecond (1001.3, 1005.9 and 998.8 ticks/s across three recordings).
* - Firmware 3.1.19: **≈3.7 ticks per second**, one tick per ~268 ms.
*
* Widely repeated documentation puts this counter at 256 kHz. That figure comes
* from reading bytes 2–5 as one 32-bit value, which folds the packet counter's
* high byte into the timestamp and multiplies the apparent rate by exactly 256.
* See `docs/protocol.md`.
*
* Because the rate differs by more than two orders of magnitude between
* firmwares, this library refuses to assume it: nothing converts ticks to
* seconds without being told which rate applies, and {@link estimateDeviceClock}
* measures it from the data rather than guessing from a version string.
*/
const DEVICE_CLOCK_HZ_MILLISECOND = 1e3;
/** Approximate rate observed on firmware 3.1.19: one tick per ~268 ms. */
const DEVICE_CLOCK_HZ_COARSE = 3.724;
/** Widest deviation from a nominal rate still treated as that family. */
const FAMILY_TOLERANCE = .1;
/**
* Measures the header counter's rate by regressing ticks against host arrival.
*
* BLE delivers in bursts, so individual points scatter badly, but the long-run
* slope survives that — which is exactly why this is a regression and not a
* first-to-last difference.
*
* Feed it at least a few seconds. Below roughly two seconds the burst structure
* dominates and the answer is not meaningful; `spanSeconds` is reported so
* callers can judge that for themselves.
*/
function estimateDeviceClock(observations) {
	const n = observations.length;
	if (n < 2) return {
		ticksPerSecond: NaN,
		family: "unknown",
		ratioToFamily: NaN,
		observations: n,
		residualStdTicks: NaN,
		spanSeconds: 0
	};
	const ordered = unwrapDeviceTicks(observations);
	const t0 = ordered[0].hostTime;
	const k0 = ordered[0].deviceTicks;
	let sumT = 0;
	let sumK = 0;
	for (const o of ordered) {
		sumT += (o.hostTime - t0) / 1e6;
		sumK += o.deviceTicks - k0;
	}
	const meanT = sumT / n;
	const meanK = sumK / n;
	let covariance = 0;
	let variance = 0;
	for (const o of ordered) {
		const dt = (o.hostTime - t0) / 1e6 - meanT;
		covariance += dt * (o.deviceTicks - k0 - meanK);
		variance += dt * dt;
	}
	const spanSeconds = (ordered[n - 1].hostTime - t0) / 1e6;
	if (variance === 0) return {
		ticksPerSecond: NaN,
		family: "unknown",
		ratioToFamily: NaN,
		observations: n,
		residualStdTicks: NaN,
		spanSeconds
	};
	const ticksPerSecond = covariance / variance;
	let sumSquaredResidual = 0;
	for (const o of ordered) {
		const predicted = meanK + ticksPerSecond * ((o.hostTime - t0) / 1e6 - meanT);
		sumSquaredResidual += (o.deviceTicks - k0 - predicted) ** 2;
	}
	const candidates = [["millisecond", DEVICE_CLOCK_HZ_MILLISECOND], ["coarse", DEVICE_CLOCK_HZ_COARSE]];
	let family = "unknown";
	let ratioToFamily = NaN;
	for (const [name, nominal] of candidates) {
		const ratio = ticksPerSecond / nominal;
		if (Math.abs(ratio - 1) <= FAMILY_TOLERANCE) {
			family = name;
			ratioToFamily = ratio;
			break;
		}
	}
	return {
		ticksPerSecond,
		family,
		ratioToFamily,
		observations: n,
		residualStdTicks: Math.sqrt(sumSquaredResidual / n),
		spanSeconds
	};
}
/** Width of the header's device counter, bytes 3–5. */
const DEVICE_COUNTER_MODULUS = 2 ** 24;
/**
* Sorts by host time and removes 24-bit wraps.
*
* The counter only runs forwards, so a drop of more than half its range between
* consecutive observations is a wrap, not a reset.
*/
function unwrapDeviceTicks(observations) {
	const ordered = [...observations].toSorted((a, b) => a.hostTime - b.hostTime);
	let offset = 0;
	let previous = ordered[0].deviceTicks;
	return ordered.map((o) => {
		if (o.deviceTicks - previous < -DEVICE_COUNTER_MODULUS / 2) offset += DEVICE_COUNTER_MODULUS;
		previous = o.deviceTicks;
		return {
			...o,
			deviceTicks: o.deviceTicks + offset
		};
	});
}
/**
* Converts header ticks to seconds since the device booted.
*
* The rate is a required argument on purpose: it is a property of the firmware,
* not of the protocol, and defaulting it would reintroduce exactly the wrong
* assumption this module exists to prevent.
*/
function deviceTicksToSeconds(deviceTicks, ticksPerSecond) {
	return deviceTicks / ticksPerSecond;
}
//#endregion
//#region src/protocol/legacy/characteristics.ts
/**
* The per-characteristic GATT layout used by every Muse before the Athena:
* Muse 2016 (MU-02), Muse 2 (MU-03, MU-06), Muse S (MS-01) and Muse S Gen 2
* (MS-02).
*
* Where the Athena multiplexes every sensor onto one characteristic, these
* devices give each signal its own. Nothing is shared between the two layouts
* except the service UUID and the command encoding, which is why they need
* separate codecs rather than a flag.
*/
const LEGACY_CONTROL_CHARACTERISTIC = "273e0001-4c4d-454d-96be-f03bac821358";
/** EEG, one characteristic per electrode, in channel order. */
const LEGACY_EEG_CHARACTERISTICS = [
	"273e0003-4c4d-454d-96be-f03bac821358",
	"273e0004-4c4d-454d-96be-f03bac821358",
	"273e0005-4c4d-454d-96be-f03bac821358",
	"273e0006-4c4d-454d-96be-f03bac821358",
	"273e0007-4c4d-454d-96be-f03bac821358"
];
const LEGACY_EEG_CHANNEL_NAMES = [
	"TP9",
	"AF7",
	"AF8",
	"TP10",
	"AUX_RIGHT"
];
const LEGACY_GYROSCOPE_CHARACTERISTIC = "273e0009-4c4d-454d-96be-f03bac821358";
const LEGACY_ACCELEROMETER_CHARACTERISTIC = "273e000a-4c4d-454d-96be-f03bac821358";
const LEGACY_TELEMETRY_CHARACTERISTIC = "273e000b-4c4d-454d-96be-f03bac821358";
/** Photoplethysmography, one characteristic per wavelength. */
const LEGACY_PPG_CHARACTERISTICS = [
	"273e000f-4c4d-454d-96be-f03bac821358",
	"273e0010-4c4d-454d-96be-f03bac821358",
	"273e0011-4c4d-454d-96be-f03bac821358"
];
const LEGACY_PPG_CHANNEL_NAMES = [
	"ambient",
	"infrared",
	"red"
];
const ROLES = new Map([
	...LEGACY_EEG_CHARACTERISTICS.map((uuid, index) => [toCanonicalCharacteristicUuid(uuid), {
		signal: "eeg",
		index
	}]),
	...LEGACY_PPG_CHARACTERISTICS.map((uuid, index) => [toCanonicalCharacteristicUuid(uuid), {
		signal: "ppg",
		index
	}]),
	[toCanonicalCharacteristicUuid(LEGACY_GYROSCOPE_CHARACTERISTIC), {
		signal: "gyroscope",
		index: 0
	}],
	[toCanonicalCharacteristicUuid(LEGACY_ACCELEROMETER_CHARACTERISTIC), {
		signal: "accelerometer",
		index: 0
	}],
	[toCanonicalCharacteristicUuid(LEGACY_TELEMETRY_CHARACTERISTIC), {
		signal: "telemetry",
		index: 0
	}]
]);
/** What a characteristic carries, or `undefined` if it is not a legacy data channel. */
function legacyRole(uuid) {
	return ROLES.get(toCanonicalCharacteristicUuid(uuid));
}
/** Every characteristic worth subscribing to on a legacy device. */
const LEGACY_DATA_CHARACTERISTICS = [
	...LEGACY_EEG_CHARACTERISTICS,
	...LEGACY_PPG_CHARACTERISTICS,
	LEGACY_GYROSCOPE_CHARACTERISTIC,
	LEGACY_ACCELEROMETER_CHARACTERISTIC,
	LEGACY_TELEMETRY_CHARACTERISTIC
];
//#endregion
//#region src/protocol/legacy/decode.ts
/**
* Decoders for the pre-Athena protocol.
*
* Every packet begins with a 16-bit big-endian sequence number, then samples.
* The packing differs from the Athena's in both width and bit order — 12-bit
* **MSB-first** here against 14-bit LSB-first there — so the two share no code.
* Reading one with the other's unpacker yields plausible-looking noise rather
* than an obvious failure, which is why they are kept firmly apart.
*/
const LEGACY_EEG_SAMPLES_PER_PACKET = 12;
const LEGACY_EEG_SAMPLE_RATE = 256;
const LEGACY_PPG_SAMPLES_PER_PACKET = 6;
const LEGACY_PPG_SAMPLE_RATE = 64;
const LEGACY_IMU_SAMPLES_PER_PACKET = 3;
const LEGACY_IMU_SAMPLE_RATE = 52;
/**
* Microvolts per count, and the count that means zero.
*
* The mid-scale subtraction is not optional: a 12-bit converter centred on
* 0x800 reads about 1000 µV without it. This mirrors what the Athena needs,
* where the same omission in published decoders shifts every value by ~725 µV.
*/
const LEGACY_EEG_MICROVOLTS_PER_COUNT = .48828125;
const LEGACY_EEG_ZERO_COUNT = 2048;
/** g and degrees per second per count, for the 16-bit signed IMU values. */
const LEGACY_ACCELEROMETER_G_PER_COUNT = 610352e-10;
const LEGACY_GYROSCOPE_DEG_PER_SECOND_PER_COUNT = .0074768;
function sequenceOf(data, expected, what) {
	if (data.length < expected) throw new MuseError("MALFORMED_PACKET", `${what} packet needs ${expected} bytes, got ${data.length}`, { details: { length: data.length } });
	return data[0] * 256 + data[1];
}
/**
* Decodes one EEG channel: 12 samples packed 12 bits each, most significant
* bit first, after the sequence number.
*/
function decodeLegacyEeg(data) {
	const sequence = sequenceOf(data, 20, "EEG");
	const values = /* @__PURE__ */ new Float32Array(12);
	for (let i = 0; i < 12; i++) {
		const bit = i * 12;
		const byte = 2 + (bit >> 3);
		const raw = (bit & 7) === 0 ? data[byte] << 4 | data[byte + 1] >> 4 : (data[byte] & 15) << 8 | data[byte + 1];
		values[i] = (raw - LEGACY_EEG_ZERO_COUNT) * LEGACY_EEG_MICROVOLTS_PER_COUNT;
	}
	return {
		sequence,
		values
	};
}
/** Decodes one PPG channel: 6 samples of 24 bits, big-endian, unscaled counts. */
function decodeLegacyPpg(data) {
	const sequence = sequenceOf(data, 20, "PPG");
	const values = /* @__PURE__ */ new Float32Array(6);
	for (let i = 0; i < 6; i++) {
		const byte = 2 + i * 3;
		values[i] = data[byte] << 16 | data[byte + 1] << 8 | data[byte + 2];
	}
	return {
		sequence,
		values
	};
}
/** Decodes accelerometer or gyroscope: 3 samples of x, y, z as 16-bit signed. */
function decodeLegacyImu(data, scale) {
	const sequence = sequenceOf(data, 20, "IMU");
	const values = /* @__PURE__ */ new Float32Array(9);
	const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
	for (let sample = 0; sample < 3; sample++) for (let axis = 0; axis < 3; axis++) values[sample * 3 + axis] = view.getInt16(2 + sample * 6 + axis * 2, false) * scale;
	return {
		sequence,
		values
	};
}
/**
* Decodes the telemetry packet.
*
* The battery divisor of 512 and voltage factor of 2.2 come from the reference
* implementation; the two bytes between them are unidentified there and here.
*/
function decodeLegacyTelemetry(data) {
	const sequence = sequenceOf(data, 10, "Telemetry");
	const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
	return {
		sequence,
		batteryPercent: view.getUint16(2, false) / 512,
		fuelGaugeMillivolts: view.getUint16(4, false) * 2.2,
		temperatureCelsius: view.getUint16(8, false)
	};
}
//#endregion
//#region src/protocol/legacy/detect.ts
/**
* Works out which protocol a device speaks from the characteristics it exposes.
*
* Preferred over matching on a name or firmware string: those change, and a
* device that lies about either still cannot fake its GATT table. The Athena
* exposes a single multiplexed data characteristic and none of the per-channel
* ones; every earlier model is the reverse.
*/
function detectProtocol(characteristics) {
	const present = new Set(characteristics.map((uuid) => toCanonicalCharacteristicUuid(uuid)));
	const hasUniversal = present.has(toCanonicalCharacteristicUuid(ATHENA_UNIVERSAL_CHARACTERISTIC));
	const perChannel = LEGACY_EEG_CHARACTERISTICS.filter((uuid) => present.has(toCanonicalCharacteristicUuid(uuid))).length;
	if (hasUniversal && perChannel === 0) return {
		protocol: "universal",
		reason: "multiplexed data characteristic, no per-channel EEG"
	};
	if (perChannel >= 4) return {
		protocol: "legacy",
		reason: `${perChannel} per-channel EEG characteristics`
	};
	if (hasUniversal) return {
		protocol: "universal",
		reason: "multiplexed characteristic present alongside per-channel ones"
	};
	return {
		protocol: "unknown",
		reason: `neither layout recognised among ${characteristics.length} characteristic(s)`
	};
}
//#endregion
export { ATHENA_CONTROL_CHARACTERISTIC as $, ATHENA_PRESET_PROFILES as A, imuCountsToPhysical as B, LEGACY_PPG_CHARACTERISTICS as C, DEVICE_CLOCK_HZ_MILLISECOND as D, DEVICE_CLOCK_HZ_COARSE as E, decodeDrlRefCounts as F, ATHENA_SENSOR_SPECS as G, ATHENA_SCALING as H, decodeEegCounts as I, isKnownTag as J, PACKET_HEADER_SIZE as K, decodeImuCounts as L, profileForPreset as M, selectPreset as N, deviceTicksToSeconds as O, decodeBatteryPercent as P, ATHENA_AUX_CHARACTERISTICS as Q, decodeOpticsCounts as R, LEGACY_PPG_CHANNEL_NAMES as S, legacyRole as T, decodeAthenaNotification as U, opticsCountsToUnits as V, decodeAthenaNotificationStrict as W, LsbBitReader as X, specForTag as Y, MAX_FIELD_WIDTH as Z, LEGACY_CONTROL_CHARACTERISTIC as _, LEGACY_EEG_SAMPLE_RATE as a, isAthenaDataCharacteristic as at, LEGACY_EEG_CHARACTERISTICS as b, LEGACY_IMU_SAMPLES_PER_PACKET as c, ATHENA_EEG_CHANNELS_8 as ct, LEGACY_PPG_SAMPLE_RATE as d, MUSE_COMMANDS as dt, ATHENA_DATA_CHARACTERISTICS as et, decodeLegacyEeg as f, decodeCommand as ft, LEGACY_ACCELEROMETER_CHARACTERISTIC as g, decodeLegacyTelemetry as h, LEGACY_EEG_SAMPLES_PER_PACKET as i, isAthenaControlCharacteristic as it, isPresetToken as j, estimateDeviceClock as k, LEGACY_IMU_SAMPLE_RATE as l, eegChannelLabels as lt, decodeLegacyPpg as m, presetCommand as mt, LEGACY_ACCELEROMETER_G_PER_COUNT as n, MUSE_SERVICE_UUID as nt, LEGACY_EEG_ZERO_COUNT as o, ATHENA_CHANNEL_INFO as ot, decodeLegacyImu as p, encodeCommand as pt, SUBPACKET_HEADER_SIZE as q, LEGACY_EEG_MICROVOLTS_PER_COUNT as r, MUSE_SERVICE_UUID_16 as rt, LEGACY_GYROSCOPE_DEG_PER_SECOND_PER_COUNT as s, ATHENA_EEG_CHANNELS_4 as st, detectProtocol as t, ATHENA_UNIVERSAL_CHARACTERISTIC as tt, LEGACY_PPG_SAMPLES_PER_PACKET as u, scalpChannelIndices as ut, LEGACY_DATA_CHARACTERISTICS as v, LEGACY_TELEMETRY_CHARACTERISTIC as w, LEGACY_GYROSCOPE_CHARACTERISTIC as x, LEGACY_EEG_CHANNEL_NAMES as y, eegCountsToMicrovolts as z };

//# sourceMappingURL=protocol-Bv-zQkPS.js.map